import * as React from 'react';

import { cva } from 'class-variance-authority';
import { LucideArrowLeft, LucideAsterisk } from 'lucide-react';
import {
  Group as AriaGroup,
  GroupProps as AriaGroupProps,
  Header as AriaHeader,
  Label as AriaLabel,
  LabelProps as AriaLabelProps,
  Text as AriaText,
  TextProps as AriaTextProps,
  FieldErrorContext,
  GroupRenderProps,
  ValidationResult,
} from 'react-aria-components';

import { cn } from '../../lib/stylingUtils';

import { Button } from './buttons';
import { Col, Row } from './containers';

export const requiredLabelStyle = '[&[aria-required="true"]_svg]:opacity-100';

export const labelVariants = cva([
  'flex min-h-[16px] items-center justify-between gap-1 text-sm leading-none font-medium whitespace-nowrap',
  /* Disabled */
  'rac-disabled:cursor-not-allowed rac-disabled:opacity-70',
  /* Invalid */
  'group-data-[invalid]:text-failure',
  /* Required */
  requiredLabelStyle,
]);

/**
 * Shared className constants for field components
 */
export const fieldInnerLabelClasses = {
  base: 'pt-0.5 text-xs text-neutral/40',
  left: 'text-left',
  right: 'text-right',
} as const;

export const fieldPrefixSuffixClasses = {
  base: 'flex-shrink-0 text-sm leading-[1em] whitespace-nowrap text-neutral/40',
} as const;

export type LabelProps = AriaLabelProps & {
  adornment?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

export const Label = ({
  className,
  adornment,
  children,
  ...props
}: LabelProps) => (
  <AriaLabel className={cn('@Label', labelVariants(), className)} {...props}>
    <div className="flex items-center gap-1">
      {children}
      <LucideAsterisk className="@labelAsterisk size-3 text-failure opacity-0" />
    </div>
    {adornment && <div className="flex items-center">{adornment}</div>}
  </AriaLabel>
);

export const FormDescription = ({
  className,
  ...props
}: AriaTextProps & { className?: string; children?: React.ReactNode }) => (
  <AriaText
    className={cn('@FormDescription', 'text-sm text-neutral/60', className)}
    {...props}
    slot="description"
  />
);

export const FieldError = ({
  className,
  children,
  ...props
}: Omit<AriaTextProps, 'children'> & {
  className?: string;
  children: string | ((validation: ValidationResult) => string) | undefined;
}) => {
  const fieldErrorContext: ValidationResult = React.useContext(
    FieldErrorContext,
  ) ?? {
    isInvalid: true,
    validationErrors: [] as string[],
    validationDetails: {} as unknown as ValidityState,
  };

  if (!children) {
    return null;
  }

  return (
    <AriaText
      className={cn(
        '@FieldError',
        'text-sm font-medium text-failure',
        'rac-invalid:block',
        'rac-hidden:hidden',
        'empty:hidden',
        className,
      )}
      slot="errorMessage"
      {...props}
    >
      {typeof children === 'function' ? children(fieldErrorContext) : children}
    </AriaText>
  );
};

export const fieldGroupVariants = ({
  invalid,
  disabled,
  focusWithin,
  dark,
  variant,
  className,
}: {
  invalid?: boolean;
  disabled?: boolean;
  focusWithin?: boolean;
  dark?: boolean;
  variant?: 'default' | 'bare';
  className?: string;
}) =>
  cn(
    variant === 'bare'
      ? 'relative flex w-full text-sm outline-none'
      : cn(
          'min-h-input relative flex size-input w-full items-center overflow-hidden rounded-md border px-3 py-2 text-sm ring-[3px] ring-transparent outline-none',
          'transition-colors',
          dark
            ? 'h-8 min-h-0 border-white/20 bg-white/10 text-white'
            : 'border-neutral/20 bg-white',
          'focus-within:border-primary focus-within:ring-primary/20',
          focusWithin && 'border-primary ring-primary/20',
          invalid &&
            'border-failure focus-within:border-failure focus-within:ring-failure/20',
          disabled && 'cursor-not-allowed border-neutral/20 bg-neutral/5',
        ),
    className,
  );

type GroupProps = Omit<
  AriaGroupProps,
  'className' | 'prefix' | 'suffix' | 'innerLabel'
> & {
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  suffix?: React.ReactNode;
  prefix?: React.ReactNode;
  innerLabel?: string;
  fullWidth?: boolean;
  dark?: boolean;
  variant?: 'default' | 'bare';
  prefixAlign?: 'center' | 'start';
};

export const FieldGroup = ({
  className,
  children,
  suffix,
  prefix,
  innerLabel,
  fullWidth,
  dark,
  variant,
  prefixAlign,
  ...props
}: GroupProps) => {
  const ref = React.useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const focusableElement = ref.current?.querySelector(
      'input, textarea, select',
    ) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;

    focusableElement?.focus();

    props.onClick?.(e);
  };

  return (
    <AriaGroup
      ref={ref}
      className={(state: GroupRenderProps) =>
        cn(
          '@FieldGroup',
          fieldGroupVariants({
            disabled: state.isDisabled,
            invalid: state.isInvalid,
            focusWithin: state.isFocusWithin,
            dark,
            variant,
          }),
          '[&_input]:w-full [&_input]:border-0 [&_input]:bg-transparent [&_input]:text-sm [&_input]:ring-0 [&_input]:outline-none',
          '[&_textarea]:w-full [&_textarea]:border-0 [&_textarea]:bg-transparent [&_textarea]:text-sm [&_textarea]:ring-0 [&_textarea]:outline-none',
          dark && '[&_input]:placeholder:text-white/40',
          fullWidth && 'w-full',
          className,
        )
      }
      {...props}
      onClick={handleClick}
    >
      {(
        renderProps: GroupRenderProps & {
          defaultChildren: React.ReactNode | undefined;
        },
      ) => (
        <div className="flex size-full flex-col">
          {innerLabel && (
            <div
              className={cn(
                fieldInnerLabelClasses.base,
                fieldInnerLabelClasses.left,
              )}
            >
              {innerLabel}
            </div>
          )}
          <div
            className={cn(
              'flex size-full gap-1',
              prefixAlign === 'start' ? 'items-start' : 'items-center',
            )}
          >
            {prefix && (
              <div className={fieldPrefixSuffixClasses.base}>{prefix}</div>
            )}
            {typeof children === 'function' ? children(renderProps) : children}
            {suffix && (
              <div className={fieldPrefixSuffixClasses.base}>{suffix}</div>
            )}
          </div>
        </div>
      )}
    </AriaGroup>
  );
};

export const Header = ({
  className,
  ...props
}: AriaTextProps & { className?: string }) => (
  <AriaHeader className={cn('@Header', className)} {...props} />
);

export type NewHeaderProps = {
  className?: string;
  title: React.ReactNode;
  titleAdornment?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  borderBottom?: boolean;
  borderTop?: boolean;
  onBackPressed?: () => void;
};

export const NewHeader = ({
  className,
  title,
  subtitle,
  actions,
  borderBottom = true,
  borderTop = false,
  onBackPressed,
  titleAdornment,
}: NewHeaderProps) => (
  <Row
    className={cn(
      '@NewHeader gap-4 p-4',
      borderBottom && 'border-b border-neutral/15',
      borderTop && 'border-t border-neutral/15',
      className,
    )}
  >
    {onBackPressed && (
      <Button
        variant="outline"
        onPress={onBackPressed}
        className="px-3"
        icon={<LucideArrowLeft className="size-5" />}
      />
    )}
    <Col>
      <Row gap2>
        <div className={'font-semibold'}>{title}</div>
        {titleAdornment && <Row>{titleAdornment}</Row>}
      </Row>
      {subtitle && <div className={'text-sm text-neutral/50'}>{subtitle}</div>}
    </Col>
    <Row className={'ml-auto'}>{actions}</Row>
  </Row>
);

export const Text = (
  props: AriaTextProps & {
    className?: string;
    children?: React.ReactNode;
    slot?: string;
  },
) => <AriaText {...props} className="@Text" />;
